import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle, MailWarning } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useUser } from '../context/UserContext';
import { useSettings } from '../context/SettingsContext';
import usePageSEO from '../hooks/usePageSEO';
import { getLocalizedSiteName } from '../lib/siteName';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { verifyEmail } = useUser();
  const { settings } = useSettings();
  const { toast } = useToast();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('auth.verifyingEmailMessage');
  const submittedRef = useRef(false);

  usePageSEO({ title: t('auth.verifyEmail'), noIndex: true });
  const lang = i18n.language?.substring(0, 2) || 'en';
  const siteName = getLocalizedSiteName(settings, lang);

  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('auth.verificationMissingToken');
      return;
    }

    const submit = async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
        setMessage('auth.emailVerifiedMessage');
        toast({ title: t('auth.emailVerified'), description: t('auth.nowSignedIn', { name: siteName }) });
        setTimeout(() => navigate('/account'), 1200);
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'auth.verificationInvalidOrExpired');
      }
    };

    submit();
  }, [navigate, searchParams, toast, verifyEmail, t, siteName]);

  const isSuccess = status === 'success';

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardContent className="p-8 text-center">
          {isSuccess ? (
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-5" />
          ) : (
            <MailWarning className="h-16 w-16 text-red-500 mx-auto mb-5" />
          )}
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {isSuccess ? t('auth.emailVerified') : status === 'loading' ? t('auth.verifyingEmail') : t('auth.verificationFailed')}
          </h1>
          <p className="text-gray-600 mb-6">{message.startsWith('auth.') ? t(message, { name: siteName }) : message}</p>
          {status === 'error' && (
            <Button className="bg-red-600 hover:bg-red-700" asChild>
              <Link to="/login">{t('auth.backToSignIn')}</Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
